import React, { useState, useEffect, useMemo } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import get from 'lodash/get';
import { useForm } from 'react-hook-form';
import { Popover } from 'react-tiny-popover';
import PropTypes from 'prop-types';

import Button from '../../../../components/Button';
import Input from '../../../../components/Form/Input';
import { nextAction } from '../../../../saga/actions/workflow';
import { maskPhoneNumber, matchPhoneNumber, unmaskPhoneNumber, matchEmail, maskedPhoneNumber } from '../../../../utils/masks';
import { getHTTPErrorMessage } from '../../../../utils/fetch';
import { getNextUrl } from '../url';

const Step2 = (props) => {
  const { workflow } = props;
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  // get functions to build form with useForm() hook
  const hookForm = useForm({ reValidateMode: 'onSubmit' });
  const { handleSubmit, setValue } = hookForm;

  const [isPrefilled, setIsPrefilled] = useState(false);
  const email = useMemo(() => get(workflow, 'data.data.email'), [workflow]);
  const mobile = useMemo(() => get(workflow, 'data.data.mobile'), [workflow]);
  const errorMsg = useMemo(() => getHTTPErrorMessage(workflow.error), [workflow]);

  // set initial values
  useEffect(() => {
    setValue('email', email);
    setValue('mobile', maskedPhoneNumber(mobile));
    if (email || mobile) {
      setIsPrefilled(true);
    }
  }, [email, mobile, setValue]);

  // handle functions
  const onSubmit = (data) => {
    props.nextAction({
      url: getNextUrl(),
      method: 'PUT',
      data: {
        email: data.email,
        mobile: unmaskPhoneNumber(data.mobile),
        consentToReceiveMessages: true,
      },
    });
    return false;
  };
  const validateEmail = (emailData) => {
    if (!emailData) {
      return 'Email is required';
    }
    if (!matchEmail(emailData)) {
      return 'Email is invalid';
    }
    return null;
  };

  const validatePhoneNumber = (value) => {
    if (!value) {
      return 'This input field is required';
    }
    if (!matchPhoneNumber.test(value)) {
      return 'Invalid phone number';
    }
    return null;
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="text-center md:text-left font-bold text-[26px] leading-[26px] md:text-[32px] md:leading-[38px] text-[#263952]">
        We need some
        {' '}
        <br />
        {' '}
        personal details from you.
      </div>
      <div className="text-center md:text-left font-normal text-[15px] leading-[20px] text-[#263952] mt-[8px] ">
        This won’t affect your credit score
        {' '}
        <sup>1</sup>
      </div>
      { isPrefilled
        ? (
          <>
            <div className="text-center md:text-left font-medium text-[18px] leading-[32px] text-black mt-[53px]">
              Please confirm your email and phone number
            </div>
            <div className="text-center md:text-left font-normal text-[12px] leading-[20px] text-[#9B9B9B] mt-[5px]">
              You can edit this information as needed
            </div>
          </>
        )
        : (
          <div className="text-center md:text-left font-medium text-[18px] leading-[32px] text-black mt-[53px]">
            What’s your email and phone number?
          </div>
        )}
      <Input
        autoFocus
        name="email"
        label="Email Address"
        className="w-full md:w-2/3"
        isMasked={false}
        hookForm={hookForm}
        registerOptions={{
          required: 'Email is required',
          validate: validateEmail,
        }}
      />
      <Input
        name="mobile"
        label="Phone Number"
        className="w-full md:w-2/3"
        isMasked={maskPhoneNumber}
        hookForm={hookForm}
        validate={validatePhoneNumber}
      />
      <div className="text-left font-medium text-[14px] leading-[16px] text-black mt-[15px]">
        I consent to receive calls and texts for any purpose, including marketing, under these
        <Popover
          isOpen={isPopoverOpen}
          positions={['top']} // preferred positions by priority
          align="start"
          padding={2}
          onClickOutside={() => setIsPopoverOpen(false)}
          content={(
            <>
              <div className="rounded-md border border-[#00214732] p-5 bg-[#ffffff] m-auto max-w-[90%] md:max-w-[400px] max-h-[500px] overflow-y-auto text-[#3f556e] text-[14px] scrollbar">
                <strong>Terms</strong>
                <br />
                <br />
                By providing the telephone number above and clicking &quot;Continue&quot;, you confirm you are the owner of the phone number(s), or that the owner authorized you to provide the phone number(s), or that you are the regular user of this phone number(s). You are providing express written consent via your electronic signature to receive any type of communication for any purpose, including telemarketing communications, from our
                {' '}
                <a className=" underline text-[#0000ff] " href="https://lendingusa.com/lending-partners/" target="_blank" rel="noreferrer">lending partners</a>
                , LendingUSA, their agents, representatives, affiliates, assignees or anyone calling on their behalf, at the telephone number(s) provided for any lawful purpose, even if that number is on a federal or state do-not-call list. The scope of this consent includes, but is not limited to, receiving live operator calls, calls and texts via an automatic telephone dialing system (auto-dialer), prerecorded and artificial voice messages, text/SMS messages or other means. These telephone calls and text messages may incur message, data and access fees from your telephone provider. Consent is not a condition to obtain a loan.
                <br />
                <br />
                You understand that you can request a paper copy of these terms or may withdraw your consent at any time by:
                <ul style={{ listStyle: 'unset', marginLeft: '10px', paddingLeft: '10px' }}>
                  <li>mailing your written request to LendingUSA c/o Compliance Department at 15303 Ventura Blvd., Suite 850, Sherman Oaks, CA 91403;</li>
                  <li>
                    by e-mailing
                    <a className=" underline text-[#0000ff] " href="mailto:customercare@lendingusa.com">customercare@lendingusa.com</a>
                    ; or
                  </li>
                  <li>by calling LendingUSA at (800) 994-6177.</li>
                </ul>
                <br />
                You may also reply “STOP” to opt out of receiving autodialed text messages only.
              </div>
              <span className="arrow left-2 hidden md:block" />
            </>
          )}
        >
          <span className="pl-[5px] underline cursor-pointer" onClick={() => setIsPopoverOpen(!isPopoverOpen)}>terms</span>
        </Popover>
        .
      </div>
      <Button isLoading={props.workflow.isLoading} type="submit" className="w-full md:w-[240px]">Continue</Button>
      {errorMsg && <div className="mt-3 mb-3 text-[#d71f28] text-xs">{errorMsg}</div>}
    </form>
  );
};

Step2.propTypes = {
  workflow: PropTypes.object.isRequired,
  nextAction: PropTypes.func.isRequired,
};

export default compose(
  connect(
    (state) => ({
      workflow: state.workflow,
    }),
    {
      nextAction,
    }
  )
)(Step2);
